import { z } from 'zod'

/**
 * Questionnaire templates (29 Oct 2026): the questions couples answer before
 * the wedding, in order. Stored as one jsonb array on
 * questionnaire_templates.questions; this file is the shape and the only
 * place that checks it.
 *
 * A question's `id` is the key its answer is saved under, so it never changes
 * once made: Jena can reword a label and old answers still line up.
 */

export const QUESTION_TYPES = ['short', 'long', 'choice', 'file', 'note'] as const
export type QuestionType = (typeof QUESTION_TYPES)[number]

export const QUESTION_TYPE_LABEL: Record<QuestionType, string> = {
  short: 'Short answer',
  long: 'Paragraph',
  choice: 'Multiple choice',
  file: 'File upload',
  note: 'Text only (no answer)',
}

const questionSchema = z.object({
  id: z.string().regex(/^[a-z0-9_]{1,60}$/),
  type: z.enum(QUESTION_TYPES),
  label: z.string(),
  help: z.string().optional(),
  options: z.array(z.string()).optional(),
  half: z.boolean().optional(),
  required: z.boolean().optional(),
})

export type Question = z.infer<typeof questionSchema>

/** The stored array, read safely: anything malformed is dropped, not thrown. */
export function parseQuestions(value: unknown): Question[] {
  if (!Array.isArray(value)) return []
  const seen = new Set<string>()
  const out: Question[] = []
  for (const item of value) {
    const parsed = questionSchema.safeParse(item)
    if (!parsed.success || seen.has(parsed.data.id)) continue
    seen.add(parsed.data.id)
    out.push(parsed.data)
  }
  return out
}

/** A stable key from the label: "Will there be a first dance?" → "will_there_be_a_first_dance". */
export function questionId(label: string, taken: Iterable<string>): string {
  const used = new Set(taken)
  const base =
    label
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40)
      .replace(/_+$/, '') || 'question'
  if (!used.has(base)) return base
  for (let n = 2; ; n += 1) {
    const candidate = `${base}_${n}`
    if (!used.has(candidate)) return candidate
  }
}

/** What is wrong with a set of questions before it can be saved; empty when fine. */
export function questionProblems(questions: Question[]): string[] {
  const problems: string[] = []
  questions.forEach((question, index) => {
    const where = `Question ${index + 1}`
    if (!question.label.trim()) problems.push(`${where} has no wording.`)
    if (question.type === 'choice' && (question.options ?? []).filter((option) => option.trim()).length < 2)
      problems.push(`${where} needs at least two choices.`)
  })
  return problems
}

/** Tidies a question for saving: trims, and keeps only what its type uses. */
export function cleanQuestion(question: Question): Question {
  const out: Question = { id: question.id, type: question.type, label: question.label.trim() }
  const help = question.help?.trim()
  if (help) out.help = help
  if (question.type === 'choice') out.options = (question.options ?? []).map((option) => option.trim()).filter(Boolean)
  if (question.half && question.type !== 'note') out.half = true
  if (question.required && question.type !== 'note') out.required = true
  return out
}

/**
 * Rows for laying the form out: a "half" question sits beside the next one
 * when that is half too, as in Studio Ninja (first dance beside speeches).
 */
export function layoutRows(questions: Question[]): Question[][] {
  const rows: Question[][] = []
  for (let i = 0; i < questions.length; i += 1) {
    const question = questions[i]!
    const next = questions[i + 1]
    if (question.half && next?.half) {
      rows.push([question, next])
      i += 1
    } else {
      rows.push([question])
    }
  }
  return rows
}

/** A file the couple uploaded for a "file" question. */
export interface FileAnswer {
  id: string
  name: string
  /** Where the file is stored (since 29 Oct 2026); older answers point at job_files by id. */
  path?: string
}

export type Answers = Record<string, string | FileAnswer[]>

/** Answers as stored, read safely. */
export function parseAnswers(value: unknown): Answers {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const out: Answers = {}
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (typeof item === 'string') out[key] = item
    else if (Array.isArray(item))
      out[key] = item.filter(
        (file): file is FileAnswer => typeof file === 'object' && file !== null && typeof file.id === 'string' && typeof file.name === 'string',
      )
  }
  return out
}

export type QuestionnaireStatus = 'not_sent' | 'sent' | 'opened' | 'started' | 'completed'

export const QUESTIONNAIRE_STATUS_LABEL: Record<QuestionnaireStatus, string> = {
  not_sent: 'Not sent',
  sent: 'Sent',
  opened: 'Opened',
  started: 'Started',
  completed: 'Completed',
}

/** Where a sent questionnaire is: completed beats started beats opened beats sent. */
export function questionnaireStatus(row: {
  sent_at: string | null
  opened_at: string | null
  answered_at: string | null
  completed_at: string | null
}): QuestionnaireStatus {
  if (row.completed_at) return 'completed'
  if (row.answered_at) return 'started'
  if (row.opened_at) return 'opened'
  if (row.sent_at) return 'sent'
  return 'not_sent'
}

/** The message Jena sends with the link. */
export function questionnaireMessage(firstNames: string, name: string, link: string): string {
  return [
    `Hi ${firstNames || 'there'}!`,
    ``,
    `Here is your ${name}. Please fill it in closer to your wedding date, once everything is final. Your answers save as you go, so you can come back to it, and press Submit when you're done:`,
    link,
    ``,
    `Thank you!`,
    `Jenalee`,
  ].join('\n')
}
